import { useCallback, useEffect, useRef, useState } from 'react';
import { isFrameMessageFrom, UNTRUSTED_FRAME_SANDBOX } from '../../../lib/untrusted-frame.js';

/**
 * Host-owned embed viewer (CSAGE-124).
 *
 * Renders a headless (mode=embed) report into a blob iframe and provides
 * the portal's OWN toolbar — search / group / zoom / export — driving the
 * inlined runtime over the postMessage bridge, exactly like a third-party
 * host using the embed SDK. The report iframe renders no toolbar of its
 * own, so the portal owns all the chrome (no double toolbar, full width).
 *
 * CSAGE-310: the iframe is opaque-origin (no allow-same-origin, and now
 * explicitly sandboxed rather than carrying no `sandbox` attribute at
 * all); the runtime posts on the 'sage-report' channel and we reply on
 * 'sage-host'. We target '*' because there's no origin to target —
 * `isFrameMessageFrom` (event.source identity) is the real guard.
 */
export function EmbedHtmlViewer({ url, html }: { url: string; html?: string | null }): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [visible, setVisible] = useState(0);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const [zoomPct, setZoomPct] = useState(100);

  const post = useCallback((cmd: string, args?: Record<string, unknown>) => {
    const w = iframeRef.current?.contentWindow;
    if (w) w.postMessage({ channel: 'sage-host', cmd, args }, '*');
  }, []);

  // Listen for the runtime's events.
  useEffect(() => {
    function onMsg(e: MessageEvent): void {
      if (!isFrameMessageFrom(e, iframeRef.current)) return;
      const d = e.data as {
        channel?: string;
        type?: string;
        payload?: Record<string, unknown>;
      } | null;
      if (!d || d.channel !== 'sage-report') return;
      const p = (d.payload ?? {}) as {
        columns?: string[];
        totalRows?: number;
        visibleRows?: number;
        height?: number;
        zoom?: number;
        csv?: string;
        filename?: string;
        href?: string;
      };
      if (d.type === 'ready') {
        setColumns(p.columns ?? []);
        setTotal(p.totalRows ?? 0);
        setVisible(p.visibleRows ?? p.totalRows ?? 0);
      } else if (d.type === 'state') {
        if (typeof p.totalRows === 'number') setTotal(p.totalRows);
        if (typeof p.visibleRows === 'number') setVisible(p.visibleRows);
      } else if (d.type === 'zoom') {
        if (p.zoom) setZoomPct(Math.round(p.zoom * 100));
      } else if (d.type === 'export') {
        if (p.csv) downloadCsv(p.filename ?? 'report.csv', p.csv);
      } else if (d.type === 'drill') {
        if (p.href) window.open(p.href, '_blank', 'noopener');
      }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // New render → fresh document; reset the host control state (the iframe
  // remounts via key={url} and re-announces 'ready').
  useEffect(() => {
    setQuery('');
    setGroup('');
    setZoomPct(100);
  }, [url]);

  return (
    <div className="emb-viewer">
      <div className="emb-toolbar" role="toolbar" aria-label="Report controls">
        <div className="emb-search">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="search"
            value={query}
            placeholder="Search this report…"
            aria-label="Search"
            onChange={(e) => {
              setQuery(e.target.value);
              post('search', { query: e.target.value });
            }}
          />
        </div>
        <label className="emb-group">
          Group by
          <select
            value={group}
            onChange={(e) => {
              setGroup(e.target.value);
              post('groupBy', { column: e.target.value });
            }}
          >
            <option value="">None</option>
            {columns.filter(Boolean).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <span className="emb-spacer" />
        <span className="emb-count">
          {visible === total
            ? `${total.toLocaleString()} rows`
            : `${visible.toLocaleString()} of ${total.toLocaleString()}`}
        </span>
        <span className="emb-zoom" role="group" aria-label="Zoom">
          <button type="button" title="Zoom out" onClick={() => post('zoomOut')}>
            −
          </button>
          <span className="emb-zoom-lvl" title="Reset to 100%" onClick={() => post('resetZoom')}>
            {zoomPct}%
          </span>
          <button type="button" title="Zoom in" onClick={() => post('zoomIn')}>
            +
          </button>
          <button
            type="button"
            className="emb-zoom-fit"
            title="Fit to width"
            onClick={() => post('fitWidth')}
          >
            Fit
          </button>
        </span>
        <button
          type="button"
          className="emb-export"
          title="Download the rows currently shown (after search / filter / group) as CSV. Use the toolbar Export ▾ for the full dataset."
          onClick={() => post('export', { format: 'csv' })}
        >
          Export view
        </button>
      </div>
      <div className="emb-stage">
        <iframe
          key={url}
          ref={iframeRef}
          src={html ? undefined : url}
          srcDoc={html ?? undefined}
          title="Report"
          sandbox={UNTRUSTED_FRAME_SANDBOX}
          style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#fff' }}
        />
      </div>
      <EmbedStyles />
    </div>
  );
}

function downloadCsv(name: string, text: string): void {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const u = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = u;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(u);
    a.remove();
  }, 0);
}

function EmbedStyles(): JSX.Element {
  return (
    <style>{`
      .emb-viewer { display: flex; flex-direction: column; width: 100%; height: 100%; min-height: 0; }
      .emb-toolbar {
        display: flex; align-items: center; gap: var(--space-3);
        flex-wrap: wrap; padding: 8px var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
        font-size: var(--font-size-sm);
        flex: none;
      }
      .emb-search { position: relative; flex: 0 1 300px; display: flex; align-items: center; }
      .emb-search svg { position: absolute; left: 10px; width: 15px; height: 15px; color: var(--color-text-tertiary); }
      .emb-search input {
        width: 100%; padding: 7px 10px 7px 32px;
        border: 1px solid var(--color-border); border-radius: var(--radius-sm);
        font: inherit; background: var(--color-surface-1); color: var(--color-text-primary);
      }
      .emb-group { display: inline-flex; align-items: center; gap: 8px; color: var(--color-text-tertiary); }
      .emb-group select {
        padding: 7px 10px; border: 1px solid var(--color-border);
        border-radius: var(--radius-sm); font: inherit;
        background: var(--color-surface-1); color: var(--color-text-primary);
      }
      .emb-spacer { flex: 1 1 auto; }
      .emb-count { color: var(--color-text-tertiary); font-variant-numeric: tabular-nums; }
      .emb-zoom { display: inline-flex; align-items: center; border: 1px solid var(--color-border); border-radius: var(--radius-sm); overflow: hidden; background: var(--color-surface-1); }
      .emb-zoom button { appearance: none; border: 0; background: var(--color-surface-1); cursor: pointer; font: inherit; color: var(--color-text-primary); padding: 6px 10px; }
      .emb-zoom button:hover { background: var(--color-surface-2); }
      .emb-zoom-lvl { min-width: 46px; text-align: center; color: var(--color-text-tertiary); cursor: pointer; padding: 6px 4px; border-left: 1px solid var(--color-border); border-right: 1px solid var(--color-border); font-variant-numeric: tabular-nums; }
      /* CSAGE-201: raise specificity so the divider survives the .emb-zoom button { border:0 } reset. */
      .emb-zoom button.emb-zoom-fit { font-weight: var(--font-weight-semibold); border-left: 1px solid var(--color-border); }
      .emb-export {
        appearance: none; cursor: pointer; font: inherit; font-weight: var(--font-weight-semibold);
        padding: 7px 14px; border: 1px solid var(--color-border); border-radius: var(--radius-sm);
        background: var(--color-surface-2); color: var(--color-text-primary);
      }
      .emb-export:hover { background: var(--color-surface-3); }
      /* CSAGE-239: the iframe FILLS the stage (height:100%) and the report's
         own document is the single vertical scroller (sticky column headers
         keep the header row visible). The stage itself must NOT scroll — an
         outer overflow here produced a second, redundant scroll context that
         moved the whole report and cut the hero off the top. */
      .emb-stage { flex: 1; min-height: 0; overflow: hidden; background: var(--color-surface-1); }
    `}</style>
  );
}
